/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { AlertTriangle, TrendingUp, Clock } from 'lucide-react'
import { Provider } from '@/providers/llm-provider'
import {
  getTodayUsage,
  getUsagePercentage,
  isApproachingLimit,
  getTimeUntilReset,
  FREE_TIER_LIMITS
} from '@/lib/usage-tracking'

interface UsageMetricsProps {
  provider: Provider
  compact?: boolean
}

export function UsageMetrics({ provider, compact = false }: UsageMetricsProps) {
  const usage = getTodayUsage(provider)
  const { daily, minutely } = getUsagePercentage(provider)
  const approaching = isApproachingLimit(provider)
  const limits = FREE_TIER_LIMITS[provider]
  const resetTime = getTimeUntilReset()

  if (!limits || !usage) {
    return null
  }

  const requestCount = usage.requestCount
  const dailyLimit = limits.requestsPerDay

  // Determine status color
  const getStatusColor = (percent: number) => {
    if (percent >= 90) return 'text-red-500'
    if (percent >= 80) return 'text-yellow-500'
    return 'text-green-500'
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <div className="flex items-center gap-1">
          <TrendingUp className={`w-3 h-3 ${getStatusColor(daily)}`} />
          <span className="text-foreground-muted">
            {requestCount}/{dailyLimit}
          </span>
        </div>
        {approaching && (
          <AlertTriangle className="w-3 h-3 text-yellow-500" />
        )}
      </div>
    )
  }

  return (
    <div className="bg-background-tertiary border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {provider.charAt(0).toUpperCase() + provider.slice(1)} API Usage
        </h3>
        <span className="text-xs text-foreground-muted flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Resets in {resetTime}
        </span>
      </div>

      {/* Daily Usage Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-foreground-muted">Daily Requests</span>
          <span className={getStatusColor(daily)}>
            {requestCount} / {dailyLimit} ({daily.toFixed(0)}%)
          </span>
        </div>
        <div className="w-full bg-background-secondary rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              daily >= 90
                ? 'bg-red-500'
                : daily >= 80
                ? 'bg-yellow-500'
                : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(daily, 100)}%` }}
          />
        </div>
      </div>

      {/* Minutely Usage (if active) */}
      {minutely > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground-muted">Last Minute</span>
            <span className={getStatusColor(minutely)}>
              {minutely.toFixed(0)}% of {limits.requestsPerMinute} RPM
            </span>
          </div>
          <div className="w-full bg-background-secondary rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${
                minutely >= 90
                  ? 'bg-red-500'
                  : minutely >= 80
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(minutely, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Warning Message */}
      {approaching && (
        <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded p-2">
          <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-yellow-500">
            <p className="font-semibold">Approaching daily limit</p>
            <p className="text-yellow-500/80 mt-0.5">
              You've used {daily.toFixed(0)}% of your free tier quota. Consider upgrading to a paid tier for higher limits.
            </p>
          </div>
        </div>
      )}

      {/* Tokens Used */}
      {usage.totalTokens > 0 && (
        <div className="text-xs text-foreground-muted">
          Total tokens today: {usage.totalTokens.toLocaleString()}
        </div>
      )}
    </div>
  )
}

